import "@/index.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { apiPost, setToken } from "@/lib/api";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ApplyLeader from "@/pages/ApplyLeader";
import Onboarding from "@/pages/Onboarding";
import StudentHome from "@/pages/student/Home";
import ClassView from "@/pages/student/ClassView";
import MentorHome from "@/pages/mentor/MentorHome";
import StudentDetail from "@/pages/shared/StudentDetail";
import AdminPanel from "@/pages/admin/AdminPanel";
import { Toaster } from "sonner";

function FullLoader() {
  return (
    <div className="min-h-screen grid place-items-center bg-background">
      <div className="h-12 w-12 rounded-full border-4 border-ink border-t-transparent animate-spin" />
    </div>
  );
}

function homeFor(user) {
  if (!user) return "/login";
  if (user.onboarding_state !== "complete") return "/onboarding";
  if (user.role === "admin" || user.role === "org_leader") return "/admin";
  if (user.role === "mentor") return "/mentor";
  return "/home";
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarding_state !== "complete" && location.pathname !== "/onboarding")
    return <Navigate to="/onboarding" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  return <Navigate to={homeFor(user)} replace />;
}

const ROLE_STUDENT = ["student"];
const ROLE_MENTOR = ["mentor", "admin"];
const ROLE_MANAGER = ["admin", "org_leader"];

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      const sid = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
      if (!sid) return navigate("/login", { replace: true });
      try {
        const data = await apiPost("/auth/google/session", { session_token: sid });
        setToken(data.token);
        setUser(data.user);
        window.history.replaceState(null, "", window.location.pathname);
        navigate(homeFor(data.user), { replace: true });
      } catch (e) {
        window.history.replaceState(null, "", "/login");
        toast.error(e.message || "Google sign-in failed");
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);
  return <FullLoader />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/apply-leader" element={<ApplyLeader />} />

      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />

      <Route path="/home" element={<Protected roles={ROLE_STUDENT}><StudentHome /></Protected>} />
      <Route path="/class/:slug" element={<Protected roles={ROLE_STUDENT}><ClassView /></Protected>} />

      <Route path="/mentor" element={<Protected roles={ROLE_MENTOR}><MentorHome /></Protected>} />
      <Route path="/mentor/students/:id" element={<Protected roles={ROLE_MENTOR}><StudentDetail mode="mentor" /></Protected>} />

      <Route path="/admin" element={<Protected roles={ROLE_MANAGER}><AdminPanel /></Protected>} />
      <Route path="/admin/students/:id" element={<Protected roles={ROLE_MANAGER}><StudentDetail mode="manage" /></Protected>} />

      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-center" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
